import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Image } from '../../../../modules/design-system';
import type { PoolOfferListItem } from '../../../../modules/api';
import { isOfferAvailable, statusLabel } from '../offerStatus';
import {
  artInset,
  contentBottom,
  fitScale,
  lobbyChromeHeight,
  lobbyHeight,
  maxScaleForWidth,
  MOBILE_MIN_PEEK_VISIBLE_PX,
  peekPullForArt,
  scaleGeometry,
  shiftGeometry,
  spareHeight,
  withPedestal,
} from './fitToHeight';
import { useLobbyPedestal } from './useLobbyPedestal';
import { SoldOutBadge } from './SoldOutBadge';
import { useWidestPackArt } from './useWidestPackArt';

export interface SliderProps {
  offers: PoolOfferListItem[];
  activeOfferId: number | null;
  onSlideChange?: (offerId: number) => void;
}

/** Max slides rendered on each side of the active one. Only one peek
    per side — Figma 543:2118 shows exactly one neighbour visible left
    and right of the centre pack. The `+ 0.5` cull margin (in the render
    loop) keeps the incoming peek mounted while the scroll sits between two
    packs. */
const MAX_RENDER_WINDOW = 1;
/** Debounce (ms) after the last scroll event before the slider is treated
    as "settled" on a pack and reported via `onSlideChange`. */
const SETTLE_DEBOUNCE_MS = 120;
/** Desktop: minimum horizontal sliver (px) of a peek pack that must stay
    visible inside the slider container. The Figma `slotStep` is tuned for
    wide desktops; on 900–1300px viewports the left column shrinks and a
    fixed step pushes the left peek fully off-screen — the step is capped
    so at least this much of the peek always shows. */
const MIN_PEEK_VISIBLE_PX = 90;

/**
 * Figma-exact pixel geometry per breakpoint. Every value is lifted from
 * the Figma node it came from so the layout is independent of viewport
 * width — the pack always renders at its Figma size, the slider always
 * occupies its Figma height. This is what makes the page look "like the
 * mockup" on every common viewport, not a stretched mobile rectangle.
 *
 * `slotStep` is the horizontal distance (px) between the centre pack
 * and its immediate peek; kept fixed so a narrow viewport hides part of
 * the peek (matches Figma mobile) and a wide viewport reveals it fully
 * (matches Figma desktop).
 */
export interface Geometry {
  sliderHeight: number;
  pack: { w: number; h: number; top: number };
  logo: { w: number; h: number; top: number } | null;
  /** Plinth under the pack — only when the operator turns it on. */
  pedestal: { w: number; h: number; top: number } | null;
  slotStep: number;
  packPeekScale: number;
}

/** Peek packs stay nearly as bright as the centre one — in the partner
 *  mockup the neighbours read as part of the shelf, not as disabled. */
const PEEK_OPACITY = 0.9;

/** Peek pedestals fade further than the packs, as in modern, so the centre
 *  pack keeps the only full plinth. */
const PEEK_PEDESTAL_OPACITY = 0.3;

/** Bundled plinth for a pack without its own `pedestalImageUrl` (modern's). */
const DEFAULT_PEDESTAL_URL = '/templates/modern/default-piedestal.png';

/** Gap (px) between the pack's (or its pedestal's) bottom edge and the
 *  flow-positioned title when the slider is trimmed to the pack (no logo
 *  banner). With the title's own 8px top padding it makes the partner
 *  Figma's 28px from the pack art to the title (2026-10-08: "tighten up
 *  spacing between the card and the title"). */
const PACK_TITLE_GAP_PX = 20;

// Sleek has no pedestal under the pack by default (partner mockup, 2026-09;
// an operator can add one, see PEDESTAL): packs float on the page, peeks
// shrink around their own centre so they stay vertically centred on the
// middle pack. Sizes follow the partner's Figma
// (402pt frame): a 296pt-tall pack 24pt under the header, 171×240 peeks
// whose centres sit 236pt out. Pack box aspect 0.833 = natural pack PNG
// (844/1012), so `object-contain` never letterboxes it.
const MOBILE: Geometry = {
  sliderHeight: 453,
  pack:     { w: 247, h: 296, top:  24 }, // bottom y=320, aspect 0.833
  logo:     { w: 240, h: 120, top: 328 }, // 2:1 banner right under the pack
  pedestal: null,
  slotStep: 236,
  packPeekScale:     0.81,                // Figma peek 240 / 296
};

// Tablet (600 ≤ viewport < 800). No Figma reference at this width, so
// values are interpolated: pack ~30% of typical tablet viewport (768).
const TABLET: Geometry = {
  sliderHeight: 524,
  pack:     { w: 290, h: 348, top:   0 }, // aspect 0.833
  logo:     { w: 320, h: 160, top: 356 }, // aspect 2:1, right under the pack
  pedestal: null,
  slotStep: 330,
  packPeekScale:     0.78,                // slightly closer to centre than mobile
};

// Figma desktop frame 543:2118 (1535 × 1086), minus the pedestal band.
// Pack box aspect matches the source PNG's natural 844:1012 (= 0.833) so
// `object-contain` doesn't letterbox the pack; scaled ~1.2× off Figma so
// the centre pack reads as a focal point on wide viewports.
const DESKTOP: Geometry = {
  sliderHeight: 472,
  pack:     { w: 360, h: 432, top:  20 }, // bottom y=452
  logo: null,                             // desktop logo lives in PackInfoCard
  pedestal: null,
  slotStep: 460,                          // tuned so right/left peeks read like Figma 543:2118 at typical desktop widths
  packPeekScale:     0.67,                // Figma peek pack 177/265
};

// The optional pedestal per tier, sized and seated off modern's (Figma
// 786:55598 / 543:2118) so the pack stands on it: on phones a "stage" a
// touch wider than the pack, on desktop a narrow saucer under it.
const PEDESTAL = {
  mobile:  { w: 283, h: 90, top: 318 }, // modern's 252×80 × the larger sleek pack
  tablet:  { w: 285, h: 96, top: 326 },
  desktop: { w: 250, h: 85, top: 460 },
} as const;

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Coverflow slider with pixel-perfect Figma geometry per breakpoint.
 *
 *   • viewport < 800 — mobile/tablet layout (Figma `425:946`).
 *   • viewport ≥ 800 — desktop layout (Figma `543:2118`).
 *
 * Pack / logo are rendered with fixed pixel widths & heights
 * from Figma. Each slot is absolutely positioned at the slider's
 * horizontal centre and translated sideways by
 * `distance × slotStep + dragX`. The slot's vertical layout is
 * unchanged across viewport widths so the pack always reads at its
 * Figma size, independent of how much empty space surrounds it.
 *
 * Inside each slot, the pack / logo apply per-layer scale and opacity
 * pegged to the slot's `centeredness` — a fractional value that updates
 * in real time during drag so peeks shrink as the user pulls them away
 * from the centre.
 *
 * Input is a NATIVE horizontal scroll container (an invisible layer on top
 * of the coverflow), so the browser owns the gesture — iOS snap, momentum
 * and vertical-vs-horizontal axis locking come for free, and a horizontal
 * swipe can't leak into a parent page's scroll
 * (`overscroll-behavior-x: contain`). The visual coverflow is driven by the
 * scroll position; once it settles on a pack we fire `onSlideChange` and the
 * parent updates `gameId` in-state without touching the URL.
 */
export function Slider({
  offers,
  activeOfferId,
  onSlideChange,
}: SliderProps) {

  const len = offers.length;
  const externalIndex =
    activeOfferId !== null && len > 0
      ? Math.max(0, offers.findIndex((o) => o.id === activeOfferId))
      : 0;
  const offersKey = offers.map((o) => o.id).join(',');

  // Fractional pack position read from the native scroll layer: 0 = first
  // pack centred, 1.5 = halfway between the 2nd and 3rd. Drives the coverflow
  // geometry every frame so peeks scale/slide smoothly while scrolling.
  const [scrollPos, setScrollPos] = useState(externalIndex);
  const [tier, setTier] = useState<'mobile' | 'tablet' | 'desktop'>('mobile');
  const [containerW, setContainerW] = useState(0);
  const [availableH, setAvailableH] = useState(0);
  const [chromeH, setChromeH] = useState<number | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Last index reported to the parent — guards against re-emitting and against
  // echoing the parent's own `activeOfferId` update back as a change.
  const reportedIndex = useRef(externalIndex);
  // Desktop drag-to-scroll: snap is turned OFF while dragging with a mouse so
  // the content tracks the cursor instead of snapping to points mid-drag, then
  // turned back on (and snapped) on release. Touch never flips this.
  const [snapEnabled, setSnapEnabled] = useState(true);
  // While a desktop mouse drag is active, holds a fn that tears down its
  // window listeners (also called on unmount). Non-null == drag in progress.
  const dragCleanupRef = useRef<(() => void) | null>(null);
  // True while a FINGER is down on the scroll layer. Touch events are the
  // reliable signal here — iOS fires `pointercancel` the moment a touch turns
  // into a scroll, so a pointer-based flag would read "released" halfway
  // through the swipe. A touch's `touchend` always targets the element the
  // touch STARTED on, so element handlers hear the release even if the finger
  // wandered off the slider.
  const touchActive = useRef(false);
  // Last index/step/list the positioning effect applied — lets it tell an
  // external pack switch (animate) from a first mount, a resize/tier change or
  // a new offers list (jump).
  const applied = useRef({ index: externalIndex, slotStep: 0, offersKey, mounted: false });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setContainerW(el.offsetWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Cancel any pending rAF / settle timer on unmount.
  useEffect(
    () => () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      if (settleTimer.current) clearTimeout(settleTimer.current);
      dragCleanupRef.current?.();
    },
    [],
  );

  useEffect(() => {
    // Tier follows the LAYOUT width — `documentElement.clientWidth`, the
    // exact measure the pack grid's `@[600px]`/`@[800px]` container queries
    // use (scrollbar EXCLUDED). A viewport media query counts the scrollbar
    // in, so in the ~800–816px window band the slider would flip to the
    // desktop tier (no flow title — PackInfoCard owns it there) while
    // the grid is still mobile and keeps PackInfoCard hidden, leaving the
    // pack with no title at all.
    const update = () => {
      const w = document.documentElement.clientWidth;
      setTier(w >= 800 ? 'desktop' : w >= 600 ? 'tablet' : 'mobile');
      setAvailableH(lobbyHeight());
      setChromeH(lobbyChromeHeight());
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(document.documentElement);
    const root = document.getElementById('root');
    if (root) ro.observe(root);
    return () => ro.disconnect();
  }, []);

  const hasPedestal = useLobbyPedestal();
  const { widestArtAspect, artAspectOf, onPackArtLoad } = useWidestPackArt(offers);
  const tierDims =
    tier === 'desktop' ? DESKTOP : tier === 'tablet' ? TABLET : MOBILE;
  const baseDims = hasPedestal ? withPedestal(tierDims, PEDESTAL[tier]) : tierDims;
  // Phones/tablets: shrink the whole coverflow when the screen is too short
  // for the lobby to fit unscrolled (desktop keeps its Figma size); phones
  // also grow it on screens taller than the Figma frame, as far as the width
  // leaves room for the peeks. (`currentIndex` proper is derived further
  // down, from the fitted step.)
  const centredOffer = offers[Math.min(len - 1, Math.max(0, Math.round(scrollPos)))];
  const hasLogoSlot = Boolean(baseDims.logo && centredOffer?.packLogoImageUrl);
  const baseSliderHeight =
    baseDims.logo && !hasLogoSlot
      ? contentBottom(baseDims) + PACK_TITLE_GAP_PX
      : baseDims.sliderHeight;
  const maxFit =
    tier === 'mobile' && containerW > 0
      ? maxScaleForWidth(containerW, baseDims.pack.w)
      : 1;
  const fit = tier === 'desktop' ? 1 : fitScale(availableH, baseSliderHeight, maxFit, chromeH);
  // A width-capped phone pack leaves spare height: put half above the pack,
  // so the pack block sits centred over the dock instead of over an empty band.
  const topShift =
    tier === 'mobile' ? Math.round(spareHeight(availableH, baseSliderHeight, fit, chromeH) / 2) : 0;
  const dims = shiftGeometry(scaleGeometry(baseDims, fit), topShift);
  const isDesktop = tier === 'desktop';

  // Desktop peek distance adapts to the actual container width: the Figma
  // step (460) applies as long as it leaves MIN_PEEK_VISIBLE_PX of the
  // peek pack inside the container; narrower layouts (800–1300 viewports)
  // pull the peeks in so the left one never vanishes off-screen. The peek
  // slides behind the centre pack when they meet (peek slots have lower
  // z-index), which reads as regular coverflow depth. A grown phone
  // coverflow needs the same cap — its scaled step would push the peeks
  // off the screen edge.
  const isPhoneGrown = tier === 'mobile' && fit > 1;
  const peekVisualHalfW = (dims.pack.w * dims.packPeekScale) / 2;
  const minPeekVisible = isDesktop ? MIN_PEEK_VISIBLE_PX : MOBILE_MIN_PEEK_VISIBLE_PX;
  const boxStep =
    (isDesktop || isPhoneGrown) && containerW > 0
      ? Math.min(
          dims.slotStep,
          containerW / 2 + peekVisualHalfW - minPeekVisible,
        )
      : dims.slotStep;
  // Partner pack art thinner than the stock pack left the peeks off screen
  // (Betr, 2026-10-20) — close the peek's empty band so it peeks in again.
  const slotStep = boxStep - peekPullForArt(dims.pack, dims.packPeekScale, widestArtAspect);

  // Left/right spacer so the first and last pack can snap to the CENTRE of
  // the viewport: a snap point sits every `slotStep`, so `scrollLeft = i*step`
  // centres pack i and `scrollPos = scrollLeft / slotStep` is a clean index.
  const sidePad = Math.max(0, (containerW - slotStep) / 2);

  const clampIndex = (i: number) => Math.min(len - 1, Math.max(0, i));
  const currentIndex = clampIndex(Math.round(scrollPos));

  const scrollToIndex = (index: number, smooth: boolean) => {
    const el = scrollRef.current;
    if (!el || slotStep <= 0) return;
    const left = clampIndex(index) * slotStep;
    if (smooth && typeof el.scrollTo === 'function') {
      el.scrollTo({ left, behavior: 'smooth' });
    } else {
      el.scrollLeft = left;
    }
  };

  // Park the scroll layer on the active pack. ANIMATE when the pack changed
  // via an external switch (parent / PackInfoCard tier) with the layout
  // unchanged — so it slides like the pagination dots. JUMP instantly on the
  // first mount, on slotStep changes (resize / tier flip) and when the offers
  // list itself changes, so nothing slides on load or reflow. A deep link
  // first renders the lone detail pack at index 0; when the category list
  // lands the same pack sits at another index, and animating there would
  // show the first pack and slide back. The `> 1` guard skips redundant work
  // when the scroll is already parked on the target. A layout effect, so the
  // jump lands before the browser paints: a passive one let the new list paint
  // a frame at the OLD scroll position — an unloaded neighbour (blank) or the
  // pack at peek opacity in the centre, the flicker partners saw coming in
  // from their lobby (2026-10-14).
  //
  // The pack a swipe settled on comes back here as `activeOfferId` and must
  // NOT be scrolled to: the native snap already parked the scroll on it, only
  // a pixel or two off the fractional snap point that thinner custom pack art
  // makes. iOS WebKit plays a programmatic scroll on a snap container from a
  // stale snap point (bugs.webkit.org 300086 / 245722), so that nudge flung
  // the carousel back to the pack the swipe started on (Betr, 2026-10-22).
  useLayoutEffect(() => {
    const target = Math.min(len - 1, Math.max(0, externalIndex));
    const prev = applied.current;
    const smooth =
      prev.mounted &&
      target !== prev.index &&
      prev.slotStep === slotStep &&
      prev.offersKey === offersKey;
    const isSettledSwipe = smooth && target === reportedIndex.current;
    reportedIndex.current = target;
    applied.current = { index: target, slotStep, offersKey, mounted: true };
    if (isSettledSwipe) return;

    const el = scrollRef.current;
    if (el && slotStep > 0) {
      const left = target * slotStep;
      if (Math.abs(el.scrollLeft - left) > 1) {
        if (smooth && typeof el.scrollTo === 'function') {
          // onScroll drives `scrollPos` frame-by-frame during the animation,
          // so we must NOT also set it here (that would snap-then-slide).
          el.scrollTo({ left, behavior: 'smooth' });
          return;
        }
        el.scrollLeft = left;
      }
    }
    setScrollPos(target);
  }, [externalIndex, slotStep, len, offersKey]);

  // Report the pack the scroll came to rest on. Gestures in progress are
  // skipped: a mouse drag reports on release (via `finish`), and a touch swipe
  // reports on `touchend`. Reporting mid-gesture used to hand the parent a pack
  // the finger was merely PASSING OVER — the parent then set `activeOfferId`,
  // which fired the positioning effect's smooth `scrollTo` into the live
  // gesture AND stamped `reportedIndex`, so the pack the user actually released
  // on could match it and never be reported at all. Slow swipes (which pause
  // longer than the debounce mid-drag) hit that every time.
  const reportSettled = () => {
    if (dragCleanupRef.current || touchActive.current) return;
    const node = scrollRef.current;
    if (!node || slotStep <= 0) return;
    const landed = clampIndex(Math.round(node.scrollLeft / slotStep));
    if (landed !== reportedIndex.current) {
      reportedIndex.current = landed;
      const target = offers[landed];
      if (target) onSlideChange?.(target.id);
    }
  };

  const scheduleSettle = () => {
    if (settleTimer.current) clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(reportSettled, SETTLE_DEBOUNCE_MS);
  };

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || slotStep <= 0) return;
    // Coalesce to one read per frame — iOS fires scroll rapidly during
    // momentum and we only need the latest position per paint.
    if (rafRef.current === null) {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const node = scrollRef.current;
        if (node) setScrollPos(node.scrollLeft / slotStep);
      });
    }
    scheduleSettle();
  };

  // Finger down/up on the gesture layer. The release re-arms the settle check
  // because a swipe can end exactly on a snap point, in which case no further
  // scroll event follows to schedule one.
  const handleTouchStart = () => {
    touchActive.current = true;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (e.touches.length > 0) return; // another finger still down
    touchActive.current = false;
    scheduleSettle();
  };

  // Desktop only: click-and-drag the slider horizontally with a mouse.
  // Native scroll containers don't drag with a mouse, so we translate the
  // pointer delta into `scrollLeft` ourselves. Touch is left entirely to the
  // browser (the `pointerType` guard) — that's what keeps the iOS fix intact.
  //
  // Listeners live on `window`, NOT the element: setting `scrollLeft` mid-drag
  // drops pointer capture in Chrome, so an element-level `pointerup` is missed
  // when the button is released OUTSIDE the slider — the drag would then stick
  // and resume when the cursor came back. Window always hears the release, and
  // the `buttons` check is a second safety net for a swallowed pointerup.
  const handleDragStart = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse' || len < 2) return;
    const el = scrollRef.current;
    if (!el) return;
    const startX = e.clientX;
    const startScrollLeft = el.scrollLeft;
    const step = slotStep;
    const controller = new AbortController();

    const finish = () => {
      controller.abort();
      if (settleTimer.current) clearTimeout(settleTimer.current);
      dragCleanupRef.current = null;
      setSnapEnabled(true);
      const node = scrollRef.current;
      if (node && step > 0) {
        scrollToIndex(Math.round(node.scrollLeft / step), true);
      }
    };
    const onMove = (ev: PointerEvent) => {
      if ((ev.buttons & 1) === 0) {
        finish();
        return;
      }
      el.scrollLeft = startScrollLeft - (ev.clientX - startX);
    };

    setSnapEnabled(false);
    dragCleanupRef.current = () => controller.abort();
    const opts = { signal: controller.signal };
    window.addEventListener('pointermove', onMove, opts);
    window.addEventListener('pointerup', finish, opts);
    window.addEventListener('pointercancel', finish, opts);
    e.preventDefault();
  };

  if (len === 0) {
    return (
      <div ref={containerRef} className="relative w-full select-none">
        <div className="relative w-full" style={{ height: dims.sliderHeight }} />
      </div>
    );
  }

  // Chevrons sit 60px off the pack edge on wide desktops. On narrow
  // desktop columns (800–900 viewports) that offset would push them past
  // the container edge — clamp so the buttons stay fully visible,
  // overlapping the pack edge like a regular carousel when there's no
  // room beside it.
  const chevronOffset =
    containerW > 0
      ? Math.min(dims.pack.w / 2 + 60, containerW / 2 - 48)
      : dims.pack.w / 2 + 60;

  // `dims.sliderHeight` reserves vertical space for the logo banner that
  // lives inside the slider on mobile/tablet. When the active offer ships
  // without a `packLogoImageUrl`, that banner slot is empty and the text
  // title renders in normal flow BELOW the slider area (see the
  // `<div data-co-component="PackTitle">` block further down). In that
  // case we trim the slider height to "just past the pack" so there's no
  // empty gap between the pack and the text title.
  //
  // Desktop (`dims.logo === null`) shows the title via PackInfoCard, so
  // sliderHeight stays at its full DESKTOP value regardless.
  const hasLogoImage = Boolean(
    dims.logo && offers[currentIndex]?.packLogoImageUrl,
  );
  const sliderHeight =
    dims.logo && !hasLogoImage
      ? contentBottom(dims) + PACK_TITLE_GAP_PX
      : dims.sliderHeight;

  return (
    <div ref={containerRef} className="relative w-full select-none">
      {/* Visual coverflow layer — non-interactive, driven by `scrollPos`.
          Clip the peek slots HORIZONTALLY so a side pack (esp. the right
          peek, which translateX's past the viewport) can't widen the document
          — a wider document makes the `fixed left-0 right-0` bottom bar span
          the whole scroll-width (e.g. 556px) and pushes its buy button
          off-screen. `clip` + default `overflow-y:visible` keeps vertical pack
          shadows intact (they coexist per spec). Desktop keeps `visible` —
          there the bottom bar is in-flow, not fixed, so no widening problem. */}
      <div
        className="relative w-full overflow-x-clip @[800px]:overflow-x-visible"
        style={{ height: sliderHeight }}
      >
        {/* HeroBackdrop (sonar waves) is rendered by PackPage one level
            up so it bleeds *behind* the header and *past* the slider's
            own bounds, matching Figma 543:2118. Slider itself only
            renders the slots over the wave layer. */}

        {/* Chevron arrows — desktop only. Positioned right next to the
            pack (40 px gap from each pack edge) instead of at the slider
            container edges, matching Figma 543:2118 where the arrows sit
            tight against the centre pack. */}
        {isDesktop && len > 1 && (
          <>
            <button
              type="button"
              onClick={() => scrollToIndex(currentIndex - 1, true)}
              disabled={currentIndex === 0}
              aria-label="Previous pack"
              style={{ left: `calc(50% - ${chevronOffset}px)` }}
              className="
                absolute top-1/2 -translate-y-1/2 -translate-x-full z-30
                flex h-11 w-11 items-center justify-center rounded-full
                bg-black/30 text-white backdrop-blur-md
                border border-white/15
                cursor-pointer transition
                hover:bg-black/45 hover:scale-105
                disabled:cursor-default disabled:opacity-30 disabled:hover:scale-100
              "
            >
              <ChevronLeft size={20} strokeWidth={2.5} />
            </button>
            <button
              type="button"
              onClick={() => scrollToIndex(currentIndex + 1, true)}
              disabled={currentIndex === len - 1}
              aria-label="Next pack"
              style={{ left: `calc(50% + ${chevronOffset}px)` }}
              className="
                absolute top-1/2 -translate-y-1/2 z-30
                flex h-11 w-11 items-center justify-center rounded-full
                bg-black/30 text-white backdrop-blur-md
                border border-white/15
                cursor-pointer transition
                hover:bg-black/45 hover:scale-105
                disabled:cursor-default disabled:opacity-30 disabled:hover:scale-100
              "
            >
              <ChevronRight size={20} strokeWidth={2.5} />
            </button>
          </>
        )}

        {offers.map((slotOffer, sourceIndex) => {
          // `scrollPos` is fractional, so `distance` is continuous — the peek
          // scales/slides smoothly as you scroll instead of stepping per pack.
          const distance = sourceIndex - scrollPos;
          // The active pack is never culled. When a deep link's category list
          // lands, this render still has the lone pack's `scrollPos` (0) and
          // would drop the pack for a frame; remounted, its image fades back
          // in from blank — the flicker opening a pack inside its category
          // (Betr, 2026-10-23). Kept mounted, it keeps its loaded image.
          const isActive = sourceIndex === externalIndex;
          if (!isActive && Math.abs(distance) > MAX_RENDER_WINDOW + 0.5) return null;

          const slotOffsetPx = distance * slotStep;
          const centeredness = Math.max(0, 1 - Math.abs(distance));

          const packScale = lerp(dims.packPeekScale, 1, centeredness);
          const packOpacity = lerp(PEEK_OPACITY, 1, centeredness);
          const pedestalOpacity = lerp(PEEK_PEDESTAL_OPACITY, 1, centeredness);
          // A peek pack shrinks around its centre, lifting its bottom edge;
          // its pedestal rises by as much to stay under it.
          const pedestalYShift = -(dims.pack.h / 2) * (1 - packScale);
          const logoOpacity =
            centeredness > 0.7 ? (centeredness - 0.7) / 0.3 : 0;

          const slotZ = centeredness > 0.5 ? 20 : 10;
          const packTransform = `translateX(${slotOffsetPx}px) scale(${packScale})`;
          // Pinned to the art's own corner, so it waits for the art's aspect —
          // pinned to the box first, it jumped onto thin art once that loaded.
          const artAspect = artAspectOf(slotOffer);
          const hasStatusBadge = !isOfferAvailable(slotOffer.status) && artAspect !== null;

          return (
            <div
              key={`slot-${slotOffer.id}`}
              data-distance={distance}
              data-centeredness={centeredness.toFixed(2)}
              className="absolute inset-0"
              style={{
                pointerEvents: 'none',
                zIndex: slotZ,
              }}
              aria-hidden={centeredness < 0.9 || undefined}
            >
              {dims.pedestal && (
                <div
                  className="absolute"
                  style={{
                    top: dims.pedestal.top,
                    left: `calc(50% - ${dims.pedestal.w / 2}px)`,
                    width: dims.pedestal.w,
                    height: dims.pedestal.h,
                    transform: `translateX(${slotOffsetPx}px) translateY(${pedestalYShift}px) scale(${packScale})`,
                    transformOrigin: 'center top',
                    opacity: pedestalOpacity,
                    transition: 'none',
                  }}
                >
                  <Image
                    src={slotOffer.pedestalImageUrl ?? DEFAULT_PEDESTAL_URL}
                    alt=""
                    className="absolute inset-0"
                    imgClassName="absolute inset-0 h-full w-full object-contain"
                  />
                </div>
              )}

              {/* Pack — fixed px size, centred horizontally; peeks shrink
                  around their own centre so they stay vertically centred on
                  the middle pack. */}
              {slotOffer.packImageUrl && (
                <div
                  className="absolute [filter:drop-shadow(0_24px_24px_rgba(0,0,0,0.35))]"
                  style={{
                    top: dims.pack.top,
                    left: `calc(50% - ${dims.pack.w / 2}px)`,
                    width: dims.pack.w,
                    height: dims.pack.h,
                    transform: packTransform,
                    transformOrigin: 'center center',
                    opacity: packOpacity,
                    transition: 'none',
                    // Explicit (not just inherited from the slot): on Android
                    // Chrome a `filter` layer can ignore an inherited
                    // pointer-events:none and steal the swipe from the scroll
                    // layer above. Setting it here keeps the pack un-hittable.
                    pointerEvents: 'none',
                  }}
                >
                  <Image
                    src={slotOffer.packImageUrl}
                    alt={centeredness > 0.9 ? slotOffer.title : ''}
                    className="absolute inset-0"
                    imgClassName="absolute inset-0 h-full w-full object-contain"
                    loading={centeredness > 0.5 ? 'eager' : 'lazy'}
                    onLoad={(img) => onPackArtLoad(slotOffer, img)}
                  />
                </div>
              )}

              {slotOffer.packImageUrl && hasStatusBadge && (
                <SoldOutBadge
                  label={statusLabel(slotOffer.status)}
                  pack={dims.pack}
                  inset={artInset(dims.pack, artAspect)}
                  transform={packTransform}
                  opacity={packOpacity}
                />
              )}

              {/* Logo banner — mobile/tablet only, fixed px size, centred.
                  The TEXT fallback (no `packLogoImageUrl`) is rendered
                  OUTSIDE the fixed-height slider area, further down in
                  normal flow, so a wrapped title pushes What's inside
                  and the rest of the page down instead of overlapping
                  them under the slider's clipped `sliderHeight`. */}
              {dims.logo && slotOffer.packLogoImageUrl && (
                <div
                  className="absolute"
                  style={{
                    top: dims.logo.top,
                    left: `calc(50% - ${dims.logo.w / 2}px)`,
                    width: dims.logo.w,
                    height: dims.logo.h,
                    transform: `translateX(${slotOffsetPx}px)`,
                    opacity: logoOpacity,
                    transition: 'none',
                  }}
                >
                  <Image
                    src={slotOffer.packLogoImageUrl}
                    alt=""
                    className="absolute inset-0"
                    imgClassName="absolute inset-0 h-full w-full object-contain"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Native horizontal-scroll gesture layer — transparent, sits on top
          of the coverflow and OWNS the swipe. The browser handles snap,
          momentum and axis-lock, so a vertical swipe falls through to page
          scroll (`touch-action: pan-x pan-y` + no vertical room here) and a
          horizontal swipe can't leak into a parent page's scroll
          (`overscroll-behavior-x: contain`) —
          the exact leak that made packs jump when the iframe sat inside a
          scrollable parent on iOS. Only invisible snap cells live here; the
          visible packs (with their drop-shadows) stay in the layer above.
          Chevrons (z-30) render above this layer, so their clicks still land. */}
      {len > 1 && (
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          onPointerDown={handleDragStart}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
          className="absolute inset-x-0 top-0 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          style={{
            height: sliderHeight,
            zIndex: 25,
            scrollSnapType: snapEnabled ? 'x mandatory' : 'none',
            overscrollBehaviorX: 'contain',
            // Both axes allowed: horizontal is consumed by THIS scroller,
            // vertical has no room here (overflow-y:hidden) so it chains to
            // the page — i.e. you can still scroll the page by dragging on a
            // pack. `pan-x` alone would block that vertical drag entirely.
            touchAction: 'pan-x pan-y',
          }}
        >
          <div className="flex h-full w-max">
            <div aria-hidden style={{ flex: '0 0 auto', width: sidePad }} />
            {offers.map((snapOffer) => (
              <div
                key={`snap-${snapOffer.id}`}
                aria-hidden
                style={{
                  flex: '0 0 auto',
                  width: slotStep,
                  height: '100%',
                  scrollSnapAlign: 'center',
                }}
              />
            ))}
            <div aria-hidden style={{ flex: '0 0 auto', width: sidePad }} />
          </div>
        </div>
      )}

      {/* Mobile / tablet pack-title fallback — flow-positioned BELOW the
          fixed-height slider area. When the active offer has no
          `packLogoImageUrl` we render the text title here (in normal
          document flow) so wrapping a long title (e.g. "NEW BASKETBALL
          STARTER PACK" on a 375px viewport) pushes What's inside and
          the rest of the page DOWN instead of being clipped inside the
          slider's fixed `sliderHeight`.

          Desktop (where `dims.logo === null`) shows the title via
          PackInfoCard's sidebar instead — this block stays hidden there.

          Only the ACTIVE offer's title is rendered. The slot loop above
          mounts up to 3 (centre + two peeks), but peek titles fade to
          opacity 0 via `logoOpacity` and are invisible anyway. Reading
          from `offers[currentIndex]` keeps the layout height stable
          across the swipe transition. */}
      {dims.logo &&
        offers[currentIndex] &&
        !offers[currentIndex].packLogoImageUrl && (
          <div
            data-co-component="PackTitle"
            className="flex items-center justify-center px-4 pt-2 text-center"
          >
            <span
              className="text-balance"
              style={{
                fontFamily: 'var(--co-PackTitle-fontFamily, Inter)',
                fontSize: 'var(--co-PackTitle-size, 24px)',
                // Semibold 24/32 (partner Figma 2026-10-06).
                fontWeight: 'var(--co-PackTitle-weight, 600)',
                color: 'var(--co-PackTitle-color, #ffffff)',
                lineHeight: '32px',
                letterSpacing: '0.07px',
                maxWidth: '100%',
                overflowWrap: 'anywhere',
              }}
            >
              {offers[currentIndex].title}
            </span>
          </div>
        )}

      {/* Pagination dots — desktop only. Sleek shows no dots on mobile
          (partner mockup): the peeks at the screen edges already say
          "swipe".

          Active / idle colour flows through `--co-SliderDots-{active,idle}`
          so the Design Constructor (SliderDots component) can recolour
          every dot at once. Fallbacks are the original Tailwind tones
          (`bg-white` / `bg-white/40`), so an unmodified deploy stays
          pixel-identical. Hover state on idle dots is reproduced via
          a CSS custom-property nudge inside `onMouseEnter`/`Leave` —
          replacing the previous `hover:bg-white/60` arbitrary class. */}
      {len > 1 && (
        <div
          data-co-component="SliderDots"
          className="hidden @[800px]:flex items-center justify-center gap-[7px] pt-3"
        >
          {offers.map((o, i) => {
            const active = i === currentIndex;
            return (
              <button
                key={o.id}
                type="button"
                aria-label={`Go to ${o.title}`}
                aria-current={active || undefined}
                onClick={() => scrollToIndex(i, true)}
                className={
                  'rounded-full transition-all cursor-pointer ' +
                  (active ? 'h-2 w-2' : 'h-[6px] w-[6px]')
                }
                style={{
                  background: active
                    ? 'var(--co-SliderDots-active, rgba(255,255,255,1))'
                    : 'var(--co-SliderDots-idle, rgba(255,255,255,0.4))',
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
